import { HELP_CENTER_HOME_URL } from '@/utils/external-links';

// ----------------------------------------------------------------------

function path(root, sublink) {
  return `${root}${sublink}`;
}

export const ROOT_PATH = '/';
const ROOTS_AUTH = '/auth';
const ROOTS_DASHBOARD = '/dashboard';
const ROOTS_STUDENTS = '/student';
const REFUND_ROOT = '/refund';
const SUBSCRIBER_ROOT = '/subscriptions';

/**
 * Prefixo (sem o root do dashboard) das rotas do shell do resumo do produto. O
 * layout e as seções detectam "estou no shell" por ele, então muda aqui e em
 * `productsResumeShell` juntos.
 */
export const RESUME_SHELL_PREFIX = '/products/resume-shell/';

// ----------------------------------------------------------------------

export const PATH_AUTH = {
  root: ROOTS_AUTH,
  login: path(ROOTS_AUTH, '/login'),
  register: path(ROOTS_AUTH, '/register'),
  loginUnprotected: path(ROOTS_AUTH, '/login-unprotected'),
  registerUnprotected: path(ROOTS_AUTH, '/register-unprotected'),
  verify: path(ROOTS_AUTH, '/verify/'),
  resetPassword: path(ROOTS_AUTH, '/reset-password'),
  newPassword: path(ROOTS_AUTH, '/new-password'),
  createPassword: path(ROOTS_AUTH, '/create-password'),
};

export const PATH_STUDENTS = {
  root: path(ROOTS_STUDENTS, ''),
  profile: path(ROOTS_STUDENTS, '/profile'),
  courses: path(ROOTS_STUDENTS, '/courses'),
  coursesWatch: (id) => path(ROOTS_STUDENTS, `/courses/${id}`),
};

const ROOTS_MARKETPLACE = '/marketplace';

export const PATH_MARKETPLACE = {
  root: ROOTS_MARKETPLACE,
  search: path(ROOTS_MARKETPLACE, '/busca'),
  favorites: path(ROOTS_MARKETPLACE, '/favoritos'),
  product: (id) => path(ROOTS_MARKETPLACE, `/produto/${id}`),
  seller: (slug) => path(ROOTS_MARKETPLACE, `/produtor/${slug}`),
};

export const PATH_PAGE = {
  comingSoon: '/coming-soon',
  maintenance: '/maintenance',
  pricing: '/pricing',
  payment: '/payment',
  about: '/about-us',
  contact: '/contact-us',
  mcp: '/mcp',
  page403: '/403',
  page404: '/404',
  page500: '/500',
  components: '/components',
};

export const PATH_QUIZ = 'https://quiz.cakto.com.br'

export const PATH_FAQ = HELP_CENTER_HOME_URL;

/**
 * Guia da NFS-e, página estática em `public/guias/nfse/`. O caminho termina em
 * `index.html` porque o nginx serve `try_files $uri /index.html`: sem o nome do
 * arquivo, `/guias/nfse/` não é arquivo real e cai no app em vez do guia.
 */
export const PATH_NFSE_GUIDE = '/guias/nfse/index.html';

export const PATH_DASHBOARD = {
  root: `${ROOTS_DASHBOARD}/home`,
  kanban: path(ROOTS_DASHBOARD, '/kanban'),
  calendar: path(ROOTS_DASHBOARD, '/calendar'),
  fileManager: path(ROOTS_DASHBOARD, '/files-manager'),
  permissionDenied: path(ROOTS_DASHBOARD, '/permission-denied'),
  blank: path(ROOTS_DASHBOARD, '/blank'),
  business: {
    new: path(ROOTS_DASHBOARD, '/businesses/new'),
    team: path(ROOTS_DASHBOARD, '/businesses/team'),
    account: path(ROOTS_DASHBOARD, '/businesses/account'),
  },
  general: {
    home: path(ROOTS_DASHBOARD, '/home'),
    homeV2: path(ROOTS_DASHBOARD, '/home-v2'),
    operationHealth: path(ROOTS_DASHBOARD, '/operation-health'),
    analytics: path(ROOTS_DASHBOARD, '/analytics'),
    mySales: path(ROOTS_DASHBOARD, '/my-sales'),
    mySalesRefundRequests: path(ROOTS_DASHBOARD, '/my-sales/refund-requests'),
    mySalesDisputes: path(ROOTS_DASHBOARD, '/my-sales/disputes'),
    mySalesDisputeDetail: (refId) => path(ROOTS_DASHBOARD, `/my-sales/disputes/${refId}`),
    mySalesReports: path(ROOTS_DASHBOARD, '/my-sales/reports'),
    mySalesCoupons: path(ROOTS_DASHBOARD, '/my-sales/coupons'),
    subscriptions: path(ROOTS_DASHBOARD, '/subscriptions'),
    collaborators: path(ROOTS_DASHBOARD, '/collaborators'),
    financial: path(ROOTS_DASHBOARD, '/financial'),
    financialCalendar: path(ROOTS_DASHBOARD, '/financial/calendar'),
    financialPixKeys: path(ROOTS_DASHBOARD, '/financial/pix-keys'),
    financialPixSend: path(ROOTS_DASHBOARD, '/financial/pix/send'),
    financialPixCopyPaste: path(ROOTS_DASHBOARD, '/financial/pix/copy-and-paste'),
    financialPixReceive: path(ROOTS_DASHBOARD, '/financial/pix/receive'),
    financialPixArea: path(ROOTS_DASHBOARD, '/financial/pix'),
    financialPixManageKeys: path(ROOTS_DASHBOARD, '/financial/pix/keys'),
    financialPixLimits: path(ROOTS_DASHBOARD, '/financial/pix/limits'),
    financialBankingAccount: path(ROOTS_DASHBOARD, '/financial/banking-account'),
    banking: {
      root: path(ROOTS_DASHBOARD, '/banking'),
      transactions: path(ROOTS_DASHBOARD, '/banking/transactions'),
      creditCards: path(ROOTS_DASHBOARD, '/banking/credit-cards'),
      newCreditCard: path(ROOTS_DASHBOARD, '/banking/credit-cards/new'),
      emitBoleto: path(ROOTS_DASHBOARD, '/banking/boleto/emit'),
      payBoleto: path(ROOTS_DASHBOARD, '/banking/boleto/payment'),
      boletoAssistant: path(ROOTS_DASHBOARD, '/banking/boleto/assistant'),
      pixKeys: path(ROOTS_DASHBOARD, '/banking/pix/keys'),
      createCnpjAccount: path(ROOTS_DASHBOARD, '/banking/create-account/cnpj'),
      pixPay: path(ROOTS_DASHBOARD, '/banking/pix/pay'),
      pixReceive: path(ROOTS_DASHBOARD, '/banking/pix/receive'),
      ted: path(ROOTS_DASHBOARD, '/banking/ted'),
      mobileRecharge: path(ROOTS_DASHBOARD, '/banking/services/mobile-recharge'),
      giftCard: path(ROOTS_DASHBOARD, '/banking/services/gift-card'),
    },
    apps: path(ROOTS_DASHBOARD, '/apps'),
    productsResume: {
      root: (id) => path(ROOTS_DASHBOARD, `/products/resume/${id}`),
      membersArea: (id) => path(ROOTS_DASHBOARD, `/products/resume/${id}/members-area`),
      membersV2: (id) => path(ROOTS_DASHBOARD, `/products/resume/${id}/membersv2`),
      membersV3: (id) => path(ROOTS_DASHBOARD, `/products/resume/${id}/membersv3`),
      membersExternal: (id) => path(ROOTS_DASHBOARD, `/products/resume/${id}/members-external`),
      checkoutLinks: (id) => path(ROOTS_DASHBOARD, `/products/resume/${id}/checkout-links`),
      customizeCheckout: (id) => path(ROOTS_DASHBOARD, `/products/resume/${id}/customize-checkout`),
      orderBumps: (id) => path(ROOTS_DASHBOARD, `/products/resume/${id}/order-bumps`),
      offers: (id) => path(ROOTS_DASHBOARD, `/products/resume/${id}/offers`),
      coupons: (id) => path(ROOTS_DASHBOARD, `/products/resume/${id}/coupons`),
      exitPopup: (id) => path(ROOTS_DASHBOARD, `/products/resume/${id}/exit-popup`),
      pixels: (id) => path(ROOTS_DASHBOARD, `/products/resume/${id}/pixels`),
      affiliates: (id) => path(ROOTS_DASHBOARD, `/products/resume/${id}/affiliates`),
      coproduction: (id) => path(ROOTS_DASHBOARD, `/products/resume/${id}/coproduction`),
      upsell: (id) => path(ROOTS_DASHBOARD, `/products/resume/${id}/upsell`),
      whatsapp: (id) => path(ROOTS_DASHBOARD, `/products/resume/${id}/whatsapp`),
    },
    productsResumeShell: {
      root: (id) => path(ROOTS_DASHBOARD, `/products/resume-shell/${id}`),
      productInfo: (id) => path(ROOTS_DASHBOARD, `/products/resume-shell/${id}/product-info`),
      paymentModel: (id) => path(ROOTS_DASHBOARD, `/products/resume-shell/${id}/payment-model`),
      settings: (id) => path(ROOTS_DASHBOARD, `/products/resume-shell/${id}/settings`),
      membersArea: (id) => path(ROOTS_DASHBOARD, `/products/resume-shell/${id}/members-area`),
      membersV2: (id) => path(ROOTS_DASHBOARD, `/products/resume-shell/${id}/membersv2`),
      membersV3: (id) => path(ROOTS_DASHBOARD, `/products/resume-shell/${id}/membersv3`),
      membersExternal: (id) => path(ROOTS_DASHBOARD, `/products/resume-shell/${id}/members-external`),
      checkoutLinks: (id) => path(ROOTS_DASHBOARD, `/products/resume-shell/${id}/checkout-links`),
      analytics: (id) => path(ROOTS_DASHBOARD, `/products/resume-shell/${id}/analytics`),
      customizeCheckout: (id) =>
        path(ROOTS_DASHBOARD, `/products/resume-shell/${id}/customize-checkout`),
      orderBumps: (id) => path(ROOTS_DASHBOARD, `/products/resume-shell/${id}/order-bumps`),
      offers: (id) => path(ROOTS_DASHBOARD, `/products/resume-shell/${id}/offers`),
      coupons: (id) => path(ROOTS_DASHBOARD, `/products/resume-shell/${id}/coupons`),
      exitPopup: (id) => path(ROOTS_DASHBOARD, `/products/resume-shell/${id}/exit-popup`),
      pixels: (id) => path(ROOTS_DASHBOARD, `/products/resume-shell/${id}/pixels`),
      affiliates: (id) => path(ROOTS_DASHBOARD, `/products/resume-shell/${id}/affiliates`),
      coproduction: (id) => path(ROOTS_DASHBOARD, `/products/resume-shell/${id}/coproduction`),
      upsell: (id) => path(ROOTS_DASHBOARD, `/products/resume-shell/${id}/upsell`),
      whatsapp: (id) => path(ROOTS_DASHBOARD, `/products/resume-shell/${id}/whatsapp`),
      affiliateRoot: (id, shortId) =>
        path(ROOTS_DASHBOARD, `/products/resume-shell/${id}/affiliates/${shortId}`),
      affiliateCheckoutLinks: (id, shortId) =>
        path(
          ROOTS_DASHBOARD,
          `/products/resume-shell/${id}/affiliates/${shortId}/checkout-links`
        ),
      affiliateCoupons: (id, shortId) =>
        path(ROOTS_DASHBOARD, `/products/resume-shell/${id}/affiliates/${shortId}/coupons`),
      affiliateSettings: (id, shortId) =>
        path(ROOTS_DASHBOARD, `/products/resume-shell/${id}/affiliates/${shortId}/settings`),
    },
    webhooks: path(ROOTS_DASHBOARD, '/webhooks'),
    webhookLogs: (id) => path(ROOTS_DASHBOARD, `/webhooks/${id}/logs`),
    webhookSettings: (id) => path(ROOTS_DASHBOARD, `/webhooks/${id}/settings`),
    webhooksActivities: path(ROOTS_DASHBOARD, '/webhooks/activities'),
    caktoApi: path(ROOTS_DASHBOARD, '/cakto-api'),
    caktoMcp: path(ROOTS_DASHBOARD, '/cakto-mcp'),
    products: {
      root: path(ROOTS_DASHBOARD, '/products'),
      edit: (id) => path(ROOTS_DASHBOARD, `/products/${id}/edit`),
      membersArea: (id) => path(ROOTS_DASHBOARD, `/products/${id}/members-area`),
      addProducts: path(ROOTS_DASHBOARD, '/products/add-product'),
    },
    showcase: path(ROOTS_DASHBOARD, '/showcase'),
    team: path(ROOTS_DASHBOARD, '/team'),
    another_accounts: path(ROOTS_DASHBOARD, '/another-accounts'),
    affiliates: path(ROOTS_DASHBOARD, '/affiliates'),
    partners: path(ROOTS_DASHBOARD, '/partners'),
    reports: {
      root: path(ROOTS_DASHBOARD, '/reports'),
      coproduction_revenue: path(
        `${ROOTS_DASHBOARD}/reports`,
        '/coproduction-revenue?coproduction=received'
      ),
      products_revenue: path(`${ROOTS_DASHBOARD}/reports`, '/products-revenue'),
      abandoned_sales: path(`${ROOTS_DASHBOARD}/reports`, '/abandoned-sales'),
      whatsapp_recovery: path(`${ROOTS_DASHBOARD}/reports`, '/whatsapp-recovery'),
      courses_engagement: path(`${ROOTS_DASHBOARD}/reports`, '/courses-engagement'),
      affiliates_revenue: path(`${ROOTS_DASHBOARD}/reports`, '/affiliates-revenue'),
      financial_estimate: path(`${ROOTS_DASHBOARD}/reports`, '/financial-estimate'),
    },
  },
  mail: {
    root: path(ROOTS_DASHBOARD, '/mail'),
    all: path(ROOTS_DASHBOARD, '/mail/all'),
  },
  chat: {
    root: path(ROOTS_DASHBOARD, '/chat'),
    new: path(ROOTS_DASHBOARD, '/chat/new'),
    view: (name) => path(ROOTS_DASHBOARD, `/chat/${name}`),
  },
  user: {
    root: path(ROOTS_DASHBOARD, '/user'),
    new: path(ROOTS_DASHBOARD, '/user/new'),
    list: path(ROOTS_DASHBOARD, '/user/list'),
    cards: path(ROOTS_DASHBOARD, '/user/cards'),
    profile: path(ROOTS_DASHBOARD, '/profile'),
    addFees: path(ROOTS_DASHBOARD, '/add-fees'),
    edit: (name) => path(ROOTS_DASHBOARD, `/user/${name}/edit`),
    demoEdit: path(ROOTS_DASHBOARD, `/user/reece-chung/edit`),
  },
  eCommerce: {
    root: path(ROOTS_DASHBOARD, '/e-commerce'),
    shop: path(ROOTS_DASHBOARD, '/e-commerce/shop'),
    list: path(ROOTS_DASHBOARD, '/e-commerce/list'),
    checkout: path(ROOTS_DASHBOARD, '/e-commerce/checkout'),
    new: path(ROOTS_DASHBOARD, '/e-commerce/product/new'),
    view: (name) => path(ROOTS_DASHBOARD, `/e-commerce/product/${name}`),
    edit: (name) => path(ROOTS_DASHBOARD, `/e-commerce/product/${name}/edit`),
    demoEdit: path(ROOTS_DASHBOARD, '/e-commerce/product/nike-blazer-low-77-vintage/edit'),
    demoView: path(ROOTS_DASHBOARD, '/e-commerce/product/nike-air-force-1-ndestrukt'),
  },
  invoice: {
    root: path(ROOTS_DASHBOARD, '/invoice'),
    list: path(ROOTS_DASHBOARD, '/invoice/list'),
    new: path(ROOTS_DASHBOARD, '/invoice/new'),
    view: (id) => path(ROOTS_DASHBOARD, `/invoice/${id}`),
    edit: (id) => path(ROOTS_DASHBOARD, `/invoice/${id}/edit`),
    demoEdit: path(ROOTS_DASHBOARD, '/invoice/e99f09a7-dd88-49d5-b1c8-1daf80c2d7b1/edit'),
    demoView: path(ROOTS_DASHBOARD, '/invoice/e99f09a7-dd88-49d5-b1c8-1daf80c2d7b5'),
  },
  fiscal: {
    financialInvoices: path(ROOTS_DASHBOARD, '/fiscal/invoices'),
    financialInvoicesAvulsa: path(ROOTS_DASHBOARD, '/fiscal/invoices/avulsa'),
    settings: path(ROOTS_DASHBOARD, '/fiscal/settings'),
    financialInvoicesEdit: path(ROOTS_DASHBOARD, '/fiscal/settings/edit'),
    financialInvoicesRegister: path(ROOTS_DASHBOARD, '/fiscal/settings/register'),
  },
  compliance: {
    membersV3: path(ROOTS_DASHBOARD, '/compliance/members-v3'),
  },
  blog: {
    root: path(ROOTS_DASHBOARD, '/blog'),
    posts: path(ROOTS_DASHBOARD, '/blog/posts'),
    new: path(ROOTS_DASHBOARD, '/blog/new'),
    view: (title) => path(ROOTS_DASHBOARD, `/blog/post/${title}`),
    demoView: path(ROOTS_DASHBOARD, '/blog/post/apply-these-7-secret-techniques-to-improve-event'),
  },
  notifications: {
    root: path(ROOTS_DASHBOARD, '/notifications'),
  },
  coupons: {
    root: path(ROOTS_DASHBOARD, '/coupons'),
  },
  funeleiro: {
    root: 'https://app.funeleiro.com.br',
  },
};

export const PATH_CHECKOUT_BUILDER = {
  root: path('/checkout-builder', '/:id'),
};

export const PATH_PUBLIC = {
  ratingSurvey: (token) => `/survey/${token}`,
  refund: REFUND_ROOT,
  subscriber: SUBSCRIBER_ROOT,
};

export const PATH_REFUND = {
  root: REFUND_ROOT,
  purchases: `${REFUND_ROOT}/purchases`,
  request_refund: (id) => `${REFUND_ROOT}/purchases/${id}`,
};

export const PATH_SUBSCRIBER = {
  root: SUBSCRIBER_ROOT,
  list: `${SUBSCRIBER_ROOT}/list`,
  payment: (id) => `${SUBSCRIBER_ROOT}/list/${id}/payment`,
  paymentSuccess: (id) => `${SUBSCRIBER_ROOT}/list/${id}/payment/success`,
  history: (id) => `${SUBSCRIBER_ROOT}/list/${id}/history`,
};

export const PATH_DOCS = {
  root: 'https://docs.minimals.cc',
  changelog: 'https://docs.minimals.cc/changelog',
};

export const PATH_ZONE_ON_STORE = 'https://mui.com/store/items/zone-landing-page/';

export const PATH_MINIMAL_ON_STORE = 'https://mui.com/store/items/minimal-dashboard/';

export const PATH_FREE_VERSION = 'https://mui.com/store/items/minimal-dashboard-free/';

export const PATH_FIGMA_PREVIEW =
  'https://www.figma.com/file/rWMDOkMZYw2VpTdNuBBCvN/%5BPreview%5D-Minimal-Web.26.11.22?node-id=0%3A1&t=ya2mDFiuhTXXLLF1-1';
